import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  BatchDsmAppointmentAssignmentsPayload,
  CreateDsmAppointmentAssignmentPayload,
  CreateDsmAppointmentScenarioPayload,
  DsmAppointmentAssignableProject,
  DsmAppointmentAssignment,
  DsmAppointmentDealScenario,
  DsmAppointmentReorderPayload,
  DsmAppointmentScenarioDetail,
  DsmAppointmentScenarioSummary,
  DsmAppointmentStep,
  DsmAppointmentStepMemberEntry,
  DsmAppointmentStepPayload,
  ImportDsmAppointmentStepsPayload,
  SetDsmAppointmentStepMembersPayload,
  UpdateDsmAppointmentScenarioPayload
} from '../models/dsm-appointment-scenario.models';
import { DsmDealStep, DsmDealStepQuery } from '../models/dsm-contract-details.models';
import { DsmProductLibrarySubcategory } from '../models/dsm-product-library.models';

@Injectable({ providedIn: 'root' })
export class DsmAppointmentScenariosService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #baseUrl = `${inject(API_CONFIG).baseUrlV2}/dsm/scenarios`;

  readAllScenarios(includeInactive = true): Observable<DsmAppointmentScenarioSummary[]> {
    return this.#api
      .getData<DsmAppointmentScenarioSummary[]>(`${this.#baseUrl}?includeInactive=${includeInactive}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(scenarios => scenarios ?? []));
  }

  readScenario(scenarioId: string): Observable<DsmAppointmentScenarioDetail | null> {
    return this.#api
      .getData<DsmAppointmentScenarioDetail>(`${this.#baseUrl}/${scenarioId}`, {
        showLoader: false,
        showMessage: false,
        showError: false
      })
      .pipe(map(scenario => scenario ?? null));
  }

  /**
   * Scenarios assigned to the deal's project / model / elevation scope, most specific tier first.
   * Errors surface through the standard error toaster with the backend's own message.
   */
  readScenariosByDeal(dealId: string): Observable<DsmAppointmentDealScenario[]> {
    return this.#api
      .getData<DsmAppointmentDealScenario[]>(`${this.#baseUrl}/by-deal/${dealId}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(scenarios => scenarios ?? []));
  }

  readStepForDeal(dealId: string, stepId: string, query?: DsmDealStepQuery): Observable<DsmDealStep> {
    const params = new URLSearchParams();
    if (query?.subcategoryId) params.set('subcategoryId', query.subcategoryId);
    if (query?.search) params.set('search', query.search);
    const queryString = params.toString();

    return this.#api.getData<DsmDealStep>(
      `${this.#baseUrl}/by-deal/${dealId}/steps/${stepId}${queryString ? `?${queryString}` : ''}`,
      { showLoader: false, showMessage: false }
    );
  }

  /**
   * The flat list of subcategories a step actually covers: category members expand to their
   * current children, subcategory members stay exactly as picked, and the result is
   * de-duplicated. Hidden and retired rows are excluded by default so the list matches what an
   * appointment would really offer. Each row carries its parent `categoryId`.
   */
  readStepSubcategories(
    scenarioId: string,
    stepId: string,
    includeHidden = false,
    includeInactive = false
  ): Observable<DsmProductLibrarySubcategory[]> {
    return this.#api
      .getData<
        DsmProductLibrarySubcategory[]
      >(`${this.#baseUrl}/${scenarioId}/steps/${stepId}/subcategories` + `?includeHidden=${includeHidden}&includeInactive=${includeInactive}`, { showLoader: false, showMessage: false })
      .pipe(map(rows => rows ?? []));
  }

  createScenario(payload: CreateDsmAppointmentScenarioPayload): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.postData<DsmAppointmentScenarioDetail>(
      this.#baseUrl,
      { showLoader: false, showMessage: true },
      payload
    );
  }

  updateScenario(
    scenarioId: string,
    payload: UpdateDsmAppointmentScenarioPayload
  ): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.putData<DsmAppointmentScenarioDetail>(`${this.#baseUrl}/${scenarioId}`, payload, {
      showLoader: false,
      showMessage: true
    });
  }

  deleteScenario(scenarioId: string): Observable<void> {
    return this.#api.deleteData<void>(`${this.#baseUrl}/${scenarioId}`, { showLoader: false, showMessage: true });
  }

  copyScenario(scenarioId: string, name: string): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.postData<DsmAppointmentScenarioDetail>(
      `${this.#baseUrl}/${scenarioId}/copy`,
      { showLoader: false, showMessage: true },
      { name }
    );
  }

  addStep(scenarioId: string, payload: DsmAppointmentStepPayload): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.postData<DsmAppointmentScenarioDetail>(
      `${this.#baseUrl}/${scenarioId}/steps`,
      { showLoader: false, showMessage: true },
      payload
    );
  }

  importSteps(scenarioId: string, payload: ImportDsmAppointmentStepsPayload): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.postData<DsmAppointmentScenarioDetail>(
      `${this.#baseUrl}/${scenarioId}/steps/import`,
      { showLoader: false, showMessage: true },
      payload
    );
  }

  updateStep(
    scenarioId: string,
    stepId: string,
    payload: DsmAppointmentStepPayload
  ): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.putData<DsmAppointmentScenarioDetail>(`${this.#baseUrl}/${scenarioId}/steps/${stepId}`, payload, {
      showLoader: false,
      showMessage: true
    });
  }

  deleteStep(scenarioId: string, stepId: string): Observable<DsmAppointmentScenarioDetail> {
    return this.#api.deleteData<DsmAppointmentScenarioDetail>(`${this.#baseUrl}/${scenarioId}/steps/${stepId}`, {
      showLoader: false,
      showMessage: true
    });
  }

  reorderSteps(scenarioId: string, orderedIds: string[]): Observable<DsmAppointmentScenarioDetail> {
    const payload: DsmAppointmentReorderPayload = { orderedIds };

    return this.#api.putData<DsmAppointmentScenarioDetail>(`${this.#baseUrl}/${scenarioId}/steps/reorder`, payload, {
      showLoader: true,
      showMessage: false
    });
  }

  setMembers(
    scenarioId: string,
    stepId: string,
    members: DsmAppointmentStepMemberEntry[]
  ): Observable<DsmAppointmentStep> {
    const payload: SetDsmAppointmentStepMembersPayload = { members };

    return this.#api.putData<DsmAppointmentStep>(`${this.#baseUrl}/${scenarioId}/steps/${stepId}/members`, payload, {
      showLoader: false,
      showMessage: true
    });
  }

  removeMember(scenarioId: string, stepId: string, memberId: string): Observable<DsmAppointmentStep> {
    return this.#api.deleteData<DsmAppointmentStep>(
      `${this.#baseUrl}/${scenarioId}/steps/${stepId}/members/${memberId}`,
      {
        showLoader: false,
        showMessage: true
      }
    );
  }

  readAssignments(scenarioId: string): Observable<DsmAppointmentAssignment[]> {
    return this.#api
      .getData<DsmAppointmentAssignment[]>(`${this.#baseUrl}/${scenarioId}/assignments`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(assignments => assignments ?? []));
  }

  getAssignableContexts(scenarioId: string): Observable<DsmAppointmentAssignableProject[]> {
    return this.#api
      .getData<DsmAppointmentAssignableProject[]>(`${this.#baseUrl}/${scenarioId}/assignable-contexts`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(projects => projects ?? []));
  }

  addAssignmentsBatch(
    scenarioId: string,
    contexts: CreateDsmAppointmentAssignmentPayload[]
  ): Observable<DsmAppointmentAssignment[]> {
    const payload: BatchDsmAppointmentAssignmentsPayload = { contexts };

    return this.#api
      .postData<
        DsmAppointmentAssignment[]
      >(`${this.#baseUrl}/${scenarioId}/assignments/batch`, { showLoader: false, showMessage: true }, payload)
      .pipe(map(assignments => assignments ?? []));
  }

  removeAssignment(scenarioId: string, assignmentId: string): Observable<void> {
    return this.#api.deleteData<void>(`${this.#baseUrl}/${scenarioId}/assignments/${assignmentId}`, {
      showLoader: false,
      showMessage: true
    });
  }
}
